import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ExpenseStatus, PERMISSIONS } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useExpenseQuery, useVoidExpenseMutation } from '../api/expenses.api';
import { ExpenseStatusBadge } from '../components/ExpenseBadges';

function Detail({ label, value, ltr = false }: { label: string; value: string; ltr?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value}
      </dd>
    </div>
  );
}

/**
 * One expense: what was spent, on what, and by whom it was paid.
 *
 * Read-only, because it records money that already left. There is no edit and no delete — a
 * mistake is voided, which leaves both the expense and its correction in the history.
 *
 * Nothing on this page shows an outstanding amount, a balance or a way to pay it later. There is
 * nothing to pay: that is what direct-paid means, and a screen that offered "record payment" here
 * would be describing a document this system does not have.
 */
export function ExpenseDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { expenseId } = useParams<{ expenseId: string }>();
  const { can } = usePermissions();
  const { format } = useCurrency();

  const [isVoiding, setIsVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = useExpenseQuery(expenseId);
  const voidExpense = useVoidExpenseMutation();

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('expenses.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const expense = data;
  const isPosted = expense.status === ExpenseStatus.POSTED;
  // The same capability records one and corrects one: voiding is how a mis-entry is fixed.
  const canVoid = isPosted && can(PERMISSIONS.EXPENSES_MANAGE);

  const confirmVoid = () => {
    if (!reason.trim()) return;
    voidExpense.mutate(
      { id: expense.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setFeedback(t('expenses.void.done', { reference: expense.reference }));
          setIsVoiding(false);
          setReason('');
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={expense.reference}
        subtitle={t('expenses.detailSubtitle', {
          category: expense.categoryName,
          date: expense.expenseDate,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/finance/expenses')}>
              {t('expenses.backToList')}
            </Button>
            {canVoid ? (
              <Button variant="danger" onClick={() => setIsVoiding(true)}>
                {t('expenses.void.action')}
              </Button>
            ) : null}
          </div>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {expense.status === ExpenseStatus.VOIDED ? (
        <Alert tone="warning">
          <span className="flex flex-col gap-1">
            <span>
              {t('expenses.void.notice', {
                reason: expense.voidedReason ?? '',
                by: expense.voidedByName ?? t('common.unknown'),
              })}
            </span>
            <span>{t('expenses.void.notAReversal')}</span>
          </span>
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('expenses.summarySection')}
          action={<ExpenseStatusBadge status={expense.status} />}
        />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail label={t('expenses.fields.branch')} value={expense.branchName} />
            <Detail label={t('expenses.fields.category')} value={expense.categoryName} />
            <Detail label={t('expenses.fields.expenseDate')} value={expense.expenseDate} ltr />
            <Detail label={t('expenses.fields.currency')} value={expense.currencyCode} ltr />
            <Detail
              label={t('expenses.fields.method')}
              value={t(`expenses.methods.${expense.method}`)}
            />
            <Detail
              label={t('expenses.fields.externalReference')}
              value={expense.externalReference ?? '—'}
              ltr
            />
            <Detail
              label={t('expenses.fields.supplier')}
              value={expense.supplierName ?? '—'}
            />
            <Detail label={t('expenses.fields.payee')} value={expense.payee ?? '—'} />
          </dl>
        </CardBody>
        <CardBody className="border-line space-y-2 border-t text-sm">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-ink font-medium">{t('expenses.fields.amount')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(expense.amountMinor)}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('expenses.fields.countsFor')}</span>
            <span className="text-ink" dir="ltr">
              {format(expense.activeAmountMinor)}
            </span>
          </div>
          <p className="text-muted text-xs">{t('expenses.alreadyPaid')}</p>
        </CardBody>
      </Card>

      {expense.notes ? (
        <Card>
          <CardHeader title={t('expenses.fields.notes')} />
          <CardBody>
            <p className="text-ink text-sm whitespace-pre-line" dir="auto">
              {expense.notes}
            </p>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader title={t('expenses.recordedSection')} />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Detail
              label={t('expenses.fields.postedBy')}
              value={expense.postedByName ?? t('common.unknown')}
            />
            <Detail
              label={t('expenses.fields.supplierNameAr')}
              value={expense.supplierNameAr ?? '—'}
            />
          </dl>
        </CardBody>
      </Card>

      <Modal
        isOpen={isVoiding}
        onClose={() => setIsVoiding(false)}
        title={t('expenses.void.title')}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsVoiding(false)}
              disabled={voidExpense.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmVoid}
              isLoading={voidExpense.isPending}
              disabled={!reason.trim() || voidExpense.isPending}
            >
              {t('expenses.void.action')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-ink text-sm">
            {t('expenses.void.explain', { reference: expense.reference })}
          </p>
          {/* Said plainly, because someone reading this may believe the money is coming back. It is not. */}
          <ul className="text-muted list-disc space-y-1 text-sm ps-5">
            <li>{t('expenses.void.stopsCounting')}</li>
            <li>{t('expenses.void.keepsHistory')}</li>
            <li className="text-ink font-medium">{t('expenses.void.notAReversal')}</li>
          </ul>
          {voidExpense.isError ? (
            <Alert tone="error">
              {apiErrorMessage(voidExpense.error) ?? t('expenses.void.error')}
            </Alert>
          ) : null}
          <Input
            label={t('expenses.fields.voidReason')}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
